import { useEffect, useRef, useState } from "react";
import Button from "./Button";
import "./TextAreaInput.css";
import Icon from "./Icon";

type TextAreaInputProps = {
    placeholder: string;
    onSend?: (message: string) => Promise<boolean> | boolean;
    autoFocus?: boolean;
    autoIncreaseHeight?: boolean;
    value?: string;
    onChange?: (value: string) => void;
    showSendButton?: boolean;
};

function TextAreaInput(props: TextAreaInputProps) {
    const {
        placeholder,
        onSend,
        autoFocus,
        autoIncreaseHeight = true,
        value: controlledValue,
        onChange,
        showSendButton = onSend != null,
    } = props;

    const isControlled = controlledValue !== undefined;
    const [isSending, setIsSending] = useState(false);
    const [uncontrolledInput, setUncontrolledInput] = useState("");
    const input = isControlled ? controlledValue : uncontrolledInput;

    const inputRef = useRef<HTMLTextAreaElement>(null);

    const setInput = (next: string) => {
        if (!isControlled) {
            setUncontrolledInput(next);
        }
        onChange?.(next);
    };

    const clear = () => {
        setInput("");
    };

    const send = async () => {
        if (!input) return;
        if (!onSend) return;
        setIsSending(true);
        const success = await onSend(input);
        setIsSending(false);
        if (success) {
            clear();
        }
    };

    useEffect(() => {
        if (inputRef.current) {
            if (input && autoIncreaseHeight) {
                inputRef.current.style.height = `0px`;
                const height = Math.max(
                    48,
                    Math.min(inputRef.current.scrollHeight, 140),
                );
                inputRef.current.style.height = `${height}px`;
            } else {
                inputRef.current.style.height = "auto";
            }
        }
    }, [input, autoIncreaseHeight]);

    return (
        <div className="relative h-fit text-area-input">
            <textarea
                ref={inputRef}
                placeholder={placeholder}
                value={input}
                autoFocus={autoFocus}
                onChange={(e) => {
                    setInput(e.target.value);
                }}
                onKeyDown={(e) => {
                    if (!showSendButton || !onSend) return;
                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        void send();
                    }
                }}
            />

            {showSendButton ? (
                <Button.Ghost
                    onClick={send}
                    className="absolute right-8 bottom-0 text-area-input__button"
                    disabled={!input || isSending}
                >
                    <Icon.Send />
                </Button.Ghost>
            ) : null}
        </div>
    );
}

export default TextAreaInput;
